import React from "react";
import { Link } from "react-router-dom";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

  :root {
    --ink: #0f0e0d;
    --ink-muted: #5a5650;
    --ink-faint: #b8b2aa;
    --paper: #faf8f4;
    --paper-warm: #f2ede4;
    --accent: #c8392b;
    --accent-light: #f5e8e6;
    --rule: #ddd8cf;
    --serif: 'Playfair Display', Georgia, serif;
    --sans: 'DM Sans', system-ui, sans-serif;
  }

  .bl-root * { box-sizing: border-box; margin: 0; padding: 0; }
  .bl-root {
    font-family: var(--sans);
    background: var(--paper);
    color: var(--ink);
    min-height: 100vh;
  }

  /* ── Sticky Header ── */
  .bl-header {
    border-bottom: 1px solid var(--rule);
    padding: 14px 0;
    background: var(--paper);
    position: sticky; top: 0; z-index: 50;
  }
  .bl-header-inner {
    max-width: 1200px; margin: 0 auto; padding: 0 32px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .bl-header-pub {
    font-family: var(--serif); font-size: 18px; font-weight: 700; letter-spacing: -0.01em;
  }
  .bl-header-tag {
    font-size: 11px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--ink-muted); background: var(--paper-warm);
    border: 1px solid var(--rule); border-radius: 2px; padding: 4px 10px;
  }

  /* ── Hero ── */
  .bl-hero {
    max-width: 1200px; margin: 0 auto;
    padding: 72px 32px 0;
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 0 80px; align-items: end;
  }

  .bl-kicker {
    display: flex; align-items: center; gap: 12px; margin-bottom: 24px;
  }
  .bl-kicker-line { flex: 0 0 32px; height: 2px; background: var(--accent); }
  .bl-kicker-text {
    font-size: 11px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--accent);
  }

  .bl-title {
    font-family: var(--serif);
    font-size: clamp(40px, 5vw, 68px);
    font-weight: 900; line-height: 1.0; letter-spacing: -0.025em;
    color: var(--ink);
  }
  .bl-title em { font-style: italic; color: var(--accent); }

  .bl-hero-right {
    padding-bottom: 8px;
  }
  .bl-deck {
    font-size: 17px; font-weight: 300; line-height: 1.7;
    color: var(--ink-muted);
    border-left: 2px solid var(--rule); padding-left: 20px;
    margin-bottom: 28px;
  }
  .bl-hero-meta {
    display: flex; align-items: center; gap: 20px;
  }
  .bl-hero-meta-item {
    font-size: 11px; font-weight: 500; letter-spacing: 0.1em;
    text-transform: uppercase; color: var(--ink-faint);
  }
  .bl-hero-meta-dot {
    width: 3px; height: 3px; border-radius: 50%; background: var(--rule);
  }

  /* ── Divider ── */
  .bl-divider {
    max-width: 1200px; margin: 56px auto 0; padding: 0 32px;
    display: flex; align-items: center; gap: 24px;
  }
  .bl-divider hr { flex: 1; border: none; border-top: 1px solid var(--rule); }
  .bl-divider-ornament { font-family: var(--serif); font-size: 20px; color: var(--ink-faint); }

  /* ── List ── */
  .bl-list {
    max-width: 1200px; margin: 0 auto;
    padding: 0 32px 96px;
  }

  /* Featured first entry */
  .bl-featured {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 1px; background: var(--rule);
    border: 1px solid var(--rule);
    margin-top: 56px;
    text-decoration: none;
    transition: opacity 0.15s;
  }
  .bl-featured:hover .bl-featured-image img { transform: scale(1.04); }
  .bl-featured:hover .bl-featured-arrow { transform: translateX(4px); }

  .bl-featured-image {
    overflow: hidden; aspect-ratio: 4 / 3; background: var(--paper-warm);
  }
  .bl-featured-image img {
    width: 100%; height: 100%; object-fit: cover; display: block;
    transition: transform 6s ease;
  }

  .bl-featured-body {
    background: var(--paper); padding: 48px 48px;
    display: flex; flex-direction: column; justify-content: space-between;
  }
  .bl-featured-tag {
    font-size: 10px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--accent); display: flex; align-items: center; gap: 8px;
    margin-bottom: 24px;
  }
  .bl-featured-tag::after { content: ''; flex: 0 0 24px; height: 1px; background: var(--accent); }

  .bl-featured-num {
    font-family: var(--serif); font-size: 80px; font-weight: 900;
    color: var(--ink); opacity: 0.06; line-height: 1;
    margin-bottom: -16px;
  }
  .bl-featured-title {
    font-family: var(--serif); font-size: clamp(22px, 2.5vw, 32px);
    font-weight: 700; line-height: 1.2; letter-spacing: -0.01em;
    color: var(--ink); margin-bottom: 20px;
  }
  .bl-featured-desc {
    font-size: 15px; line-height: 1.65; color: var(--ink-muted);
    margin-bottom: 32px; flex: 1;
  }
  .bl-featured-footer {
    display: flex; align-items: center; justify-content: space-between;
    padding-top: 24px; border-top: 1px solid var(--rule);
  }
  .bl-featured-date {
    font-size: 11px; font-weight: 500; letter-spacing: 0.1em;
    text-transform: uppercase; color: var(--ink-faint);
  }
  .bl-featured-arrow {
    font-size: 18px; color: var(--accent);
    transition: transform 0.2s ease;
  }

  /* Regular entries */
  .bl-entries {
    margin-top: 1px; display: flex; flex-direction: column; gap: 1px;
    background: var(--rule); border: 1px solid var(--rule); border-top: none;
  }

  .bl-entry {
    background: var(--paper); padding: 32px 40px;
    display: grid; grid-template-columns: 96px 1fr auto;
    gap: 0 32px; align-items: center;
    text-decoration: none;
    transition: background 0.15s;
  }
  .bl-entry:hover { background: var(--paper-warm); }
  .bl-entry:hover .bl-entry-arrow { transform: translateX(4px); opacity: 1; }
  .bl-entry:hover .bl-entry-image img { transform: scale(1.08); }

  .bl-entry-image {
    overflow: hidden; aspect-ratio: 1; background: var(--paper-warm);
  }
  .bl-entry-image img {
    width: 100%; height: 100%; object-fit: cover; display: block;
    transition: transform 4s ease;
  }

  .bl-entry-body {}
  .bl-entry-tag {
    font-size: 10px; font-weight: 500; letter-spacing: 0.12em;
    text-transform: uppercase; color: var(--accent); margin-bottom: 6px;
  }
  .bl-entry-title {
    font-family: var(--serif); font-size: 20px; font-weight: 700;
    line-height: 1.25; letter-spacing: -0.01em; color: var(--ink);
  }

  .bl-entry-num {
    font-family: var(--serif); font-size: 48px; font-weight: 900;
    color: var(--ink); opacity: 0.07; line-height: 1;
    text-align: right;
  }
  .bl-entry-arrow {
    font-size: 18px; color: var(--accent);
    opacity: 0.4; transition: transform 0.2s ease, opacity 0.2s ease;
    margin-left: 16px;
  }

  /* ── Footer ── */
  .bl-footer {
    border-top: 1px solid var(--rule); background: var(--paper-warm);
    padding: 40px 32px; text-align: center;
  }
  .bl-footer p { font-size: 13px; color: var(--ink-faint); }
  .bl-footer strong { color: var(--ink-muted); }

  /* ── Responsive ── */
  @media (max-width: 900px) {
    .bl-hero { grid-template-columns: 1fr; gap: 32px; padding: 48px 20px 0; }
    .bl-divider { padding: 0 20px; }
    .bl-list { padding: 0 20px 64px; }
    .bl-featured { grid-template-columns: 1fr; }
    .bl-featured-body { padding: 32px 24px; }
    .bl-entry { grid-template-columns: 72px 1fr; padding: 20px 16px; }
    .bl-entry-num { display: none; }
    .bl-header-inner { padding: 0 20px; }
  }
`;

const blogs = [
  {
    id: 0,
    title: "What is Decision Integrity (Baseline: Cost, Revenue, Risk)",
    date: "Foundational",
    desc: "Most systems are optimized to act fast. Very few are designed to ask whether the decision should be made at all.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=800&auto=format&fit=crop",
    path: "/blogs/0",
  },
  {
    id: 1,
    title: "Part I : The Detention Dispute That Looked Valid — Until It Didn't",
    date: "Case Story",
    image: "https://images.unsplash.com/photo-1578575437130-527eed3abbec?q=80&w=800&auto=format&fit=crop",
    path: "/blogs/1",
  },
  {
    id: 2,
    title: "Part II :  What Changed When the System Started Enforcing It",
    date: "Before vs After",
    image: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?q=80&w=800&auto=format&fit=crop",
    path: "/blogs/2",
  },
  {
    id: 3,
    title: "Part III:  What Actually Changes When You Stop Sending Weak Charges",
    date: "Business Impact",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=800&auto=format&fit=crop",
    path: "/blogs/3",
  },
];

export default function Blogs() {
  const [featured, ...rest] = blogs;

  return (
    <>
      <style>{styles}</style>
      <div className="bl-root">

       
        {/* <header className="bl-header">
          <div className="bl-header-inner">
            <span className="bl-header-pub">Decision Integrity</span>
            
          </div>
        </header> */}

        {/* ── Hero ── */}
        <div className="bl-hero">
          <div>
            <div className="bl-kicker">
              <span className="bl-kicker-line" />
              <span className="bl-kicker-text">Decision Integrity</span>
            </div>
            <h1 className="bl-title">
              When the<br /><em>Decision</em><br />Matters
            </h1>
          </div>
          <div className="bl-hero-right">
            <p className="bl-deck">
              How decisions impact cost, revenue, and risk — and what changes when you enforce them correctly. A four-part series on building systems that hold up.
            </p>
            {/* <div className="bl-hero-meta">
              <span className="bl-hero-meta-item">4 Articles</span>
              <span className="bl-hero-meta-dot" />
              <span className="bl-hero-meta-item">~14 min total</span>
              <span className="bl-hero-meta-dot" />
              <span className="bl-hero-meta-item">Billing · Systems · Risk</span>
            </div> */}
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="bl-divider">
          <hr />
          <span className="bl-divider-ornament">§</span>
          <hr />
        </div>

        {/* ── List ── */}
        <div className="bl-list">

          {/* Featured card */}
          <Link to={featured.path} className="bl-featured">
            <div className="bl-featured-image">
              <img src={featured.image} alt={featured.title} />
            </div>
            <div className="bl-featured-body">
              <div>
                <div className="bl-featured-tag">{featured.date}</div>
                <div className="bl-featured-num">01</div>
                <h2 className="bl-featured-title">{featured.title}</h2>
                <p className="bl-featured-desc">{featured.desc}</p>
              </div>
              <div className="bl-featured-footer">
                <span className="bl-featured-date">Start here</span>
                <span className="bl-featured-arrow">→</span>
              </div>
            </div>
          </Link>

          {/* Remaining entries */}
          <div className="bl-entries">
            {rest.map((blog) => (
              <Link key={blog.id} to={blog.path} className="bl-entry">
                <div className="bl-entry-image">
                  <img src={blog.image} alt={blog.title} />
                </div>
                <div className="bl-entry-body">
                  <div className="bl-entry-tag">{blog.date}</div>
                  <div className="bl-entry-title">{blog.title}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div className="bl-entry-num">0{blog.id + 1}</div>
                  <span className="bl-entry-arrow">→</span>
                </div>
              </Link>
            ))}
          </div>

        </div>

        {/* ── Footer ── */}
        <footer className="bl-footer">
         <p><strong style={{ color: "var(--ink-muted)" }}>Documore LLC</strong> — © {new Date().getFullYear()}</p>
        </footer>

      </div>
    </>
  );
}
